import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, MessageSquare, ChevronDown, ChevronUp, Reply } from "lucide-react";

const STATUS_COLORS = {
  active: "bg-emerald-50 text-emerald-800",
  flagged: "bg-amber-50 text-amber-800",
  deleted: "bg-rose-50 text-rose-800",
};

function ThreadPanel({ message, onReply }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await onReply(message.message_id, body);
      setBody("");
      setOpen(false);
    } finally { setSending(false); }
  };

  return (
    <div className="mt-3 space-y-2">
      {message.replies?.map((r) => (
        <div key={r.reply_id} className={`rounded-xl p-3 text-sm ${r.sender_role === "vendor" ? "bg-[hsl(var(--primary))]/10 ml-4" : "bg-muted"}`}>
          <span className="font-medium">{r.sender_name}</span>
          <span className="text-xs text-muted-foreground ml-2">{r.created_at?.slice(0, 16).replace("T", " ")}</span>
          <p className="mt-1">{r.body}</p>
        </div>
      ))}
      {!open ? (
        <Button variant="ghost" size="sm" onClick={() => setOpen(true)} className="rounded-xl text-xs h-7" data-testid="vendor-reply-btn">
          <Reply className="h-3 w-3 mr-1" /> Reply
        </Button>
      ) : (
        <div className="space-y-2">
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} className="w-full rounded-xl border border-border bg-background p-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))]" placeholder="Write your reply…" data-testid="vendor-reply-input" />
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={sending || !body.trim()} className="rounded-xl h-7 text-xs" data-testid="vendor-reply-submit">{sending ? "Sending…" : "Send reply"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="rounded-xl h-7 text-xs">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function VendorMessages() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 15 });
      if (statusFilter !== "all") params.set("status", statusFilter);
      const r = await api.get(`/messages/vendor/inbox?${params}`);
      setMessages(r.data.items);
      setTotal(r.data.total);
    } catch (e) { toast.error("Failed to load messages"); }
    setLoading(false);
  }, [statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const filtered = q ? messages.filter(m => m.subject?.toLowerCase().includes(q.toLowerCase()) || m.buyer_name?.toLowerCase().includes(q.toLowerCase())) : messages;

  const reply = async (messageId, body) => {
    try {
      await api.post(`/messages/${messageId}/reply`, { body });
      toast.success("Reply sent");
      load();
    } catch (e) { toast.error("Failed to send reply"); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8" data-testid="vendor-messages-page">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Buyer Inquiries</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Messages from travelers about your listings</p>
        </div>
        <div className="text-sm text-muted-foreground">{total} total</div>
      </div>

      <div className="flex gap-3 mb-4 flex-col sm:flex-row">
        <div className="flex items-center flex-1 rounded-xl bg-card border border-border px-3">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by subject or buyer…" className="border-0 focus-visible:ring-0 bg-transparent" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="flagged">Flagged</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <div key={`skel-msg-${i}`} className="h-20 rounded-2xl bg-muted animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="vendor-messages-empty">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p>No messages yet. Inquiries from travelers will appear here.</p>
          </div>
        ) : filtered.map((m) => (
          <div key={m.message_id} className="rounded-2xl bg-card border border-border p-5" data-testid="vendor-message-card">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{m.subject}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[m.status] || "bg-muted text-foreground"}`}>{m.status}</span>
                  {m.replies?.length > 0 && <span className="text-xs text-muted-foreground">{m.replies.length} replies</span>}
                </div>
                <div className="text-sm text-muted-foreground mt-0.5">
                  <span className="font-medium text-foreground">{m.buyer_name}</span> · {m.buyer_email} · {m.created_at?.slice(0, 10)}
                </div>
                <p className="text-sm mt-2 line-clamp-2">{m.body}</p>
                <div className="text-xs text-muted-foreground mt-1">
                  Re: <Link to={`/listings/${m.listing_id}`} className="hover:underline">{m.listing_title}</Link>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setExpanded(expanded === m.message_id ? null : m.message_id)} className="shrink-0 h-7 w-7" data-testid={`expand-message-${m.message_id}`}>
                {expanded === m.message_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
            {expanded === m.message_id && <ThreadPanel message={m} onReply={reply} />}
          </div>
        ))}
      </div>

      {total > 15 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-muted-foreground">{total} messages</span>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-xl h-8">Previous</Button>
            <Button variant="ghost" disabled={page * 15 >= total} onClick={() => setPage(p => p + 1)} className="rounded-xl h-8">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
